import { C, Explainer, PhoneFrame, Tap, Tick, rise, type ExplainerProps, type SceneDef, type SceneProps } from './engine';
import { shortName } from './unit';
const T = { check0: 700, checkGap: 380, sheet: 2850, sign: 3150, signDur: 1300, tap: 4700, done: 5000, wire: 5350, fill: 5600 };

// A loose, believable signature. pathLength=1 lets us draw it with one dash.
const SIG =
  'M6 36 C 10 18, 18 6, 22 16 C 26 26, 18 42, 26 40 C 34 38, 36 18, 42 20 C 47 22, 42 36, 48 36 C 54 36, 56 24, 60 24 C 64 24, 62 34, 67 34 C 72 34, 74 22, 79 18 C 83 15, 84 26, 82 34 C 81 40, 88 36, 94 28 C 99 22, 104 26, 106 32 C 108 37, 116 30, 122 24 M 18 46 C 50 42, 96 40, 150 34';

function HandoverScene({ t, tone, u }: SceneProps) {
  const CHECKS = u.checks.slice(0, 5);
  const at = (ms: number) => t >= ms;
  const done = at(T.done);
  const ticked = CHECKS.filter((_, i) => at(T.check0 + i * T.checkGap)).length;
  const dark = tone === 'dark';
  const rows: [string, string, string][] = [
    ['Owner', 'Unknown', u.ownerShort],
    [u.Role, 'Unknown', shortName(u.partner, 24)],
    u.statusRow,
    ['Handover', 'Not done', u.doneNote],
  ];
  return (
    <>
      {/* Dealer's phone, bleeding off the bottom */}
      <PhoneFrame x={34} y={18} w={206} h={392} tone={tone}>
        {/* checklist screen */}
        <div className="xp-abs" style={{ inset: 0, padding: '36px 14px 0', visibility: done ? 'hidden' : 'visible' }}>
          <div className="xp-cap" style={{ fontSize: 8, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {u.partner}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-.02em', marginTop: 3 }}>{u.form}</div>
          <div className="xp-mono" style={{ fontSize: 8.5, color: C.muted, marginTop: 2 }}>
            {u.model} · {u.serial}
          </div>
          <div className="xp-row" style={{ marginTop: 10, gap: 8 }}>
            <div style={{ flex: 1, height: 4, borderRadius: 4, background: C.line, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(ticked / CHECKS.length) * 100}%`, background: C.grad, transition: 'width .35s var(--xe)' }} />
            </div>
            <span className="xp-mono" style={{ fontSize: 8.5, color: C.muted, width: 30, textAlign: 'right' }}>
              {ticked}/{CHECKS.length}
            </span>
          </div>
          <div style={{ marginTop: 8 }}>
            {CHECKS.map((c, i) => {
              const on = at(T.check0 + i * T.checkGap);
              return (
                <div key={c} className="xp-row" style={{ gap: 9, height: 29, borderBottom: `1px solid ${C.line}`, fontSize: 10.5, fontWeight: 500, color: on ? C.indigo : C.muted, transition: 'color .2s' }}>
                  <span className="xp-row" style={{ width: 16, height: 16, flex: 'none', borderRadius: 5, justifyContent: 'center', background: on ? C.emerald : '#fff', boxShadow: on ? 'none' : `inset 0 0 0 1.5px #CFCDE2`, transform: on ? 'scale(1)' : 'scale(.92)', transition: 'background .18s, transform .3s var(--xe)' }}>
                    <Tick on={on} size={11} />
                  </span>
                  {c}
                </div>
              );
            })}
          </div>
          {/* signature sheet */}
          <div
            className="xp-abs"
            style={{
              left: 0,
              right: 0,
              bottom: 0,
              height: 196,
              background: '#fff',
              borderRadius: '16px 16px 0 0',
              boxShadow: '0 -14px 30px -18px rgba(24,19,67,.45), 0 0 0 1px #ECEBF5',
              padding: '10px 14px',
              transform: at(T.sheet) ? 'translateY(0)' : 'translateY(105%)',
              transition: 'transform .5s var(--xe)',
            }}
          >
            <div style={{ width: 30, height: 3, borderRadius: 3, background: C.line, margin: '0 auto 8px' }} />
            <div className="xp-row" style={{ justifyContent: 'space-between' }}>
              <span className="xp-cap" style={{ fontSize: 8, color: C.muted }}>
                {u.signLabel}
              </span>
              <span style={{ fontSize: 9.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 96 }}>{shortName(u.signer, 14)}</span>
            </div>
            <div style={{ position: 'relative', height: 62, marginTop: 6, borderRadius: 8, background: C.alt, boxShadow: `inset 0 0 0 1px ${C.line}` }}>
              <svg width="164" height="56" viewBox="0 0 160 52" style={{ position: 'absolute', left: 6, top: 3 }}>
                <path d={SIG} pathLength={1} fill="none" stroke={C.indigo} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="1" strokeDashoffset={at(T.sign) ? 0 : 1} style={{ transition: `stroke-dashoffset ${T.signDur}ms cubic-bezier(.45,.1,.4,1)` }} />
              </svg>
              <span className="xp-abs" style={{ left: 10, right: 10, bottom: 12, height: 1, background: '#D9D8E8' }} />
            </div>
            <div style={{ marginTop: 12, height: 34, borderRadius: 34, background: C.indigo, color: '#fff', fontSize: 11, fontWeight: 600, display: 'grid', placeItems: 'center', transform: at(T.tap) ? 'scale(.97)' : 'none', transition: 'transform .12s' }}>Complete handover</div>
            <Tap x={82} y={137} on={at(T.tap) && !done} />
          </div>
        </div>

        {/* success screen (a cut, not a fade) */}
        <div className="xp-abs" style={{ inset: 0, padding: '40px 14px 0', visibility: done ? 'visible' : 'hidden', background: '#fff' }}>
          <div className="xp-a" style={{ ...rise(done, 0, 10), width: 46, height: 46, borderRadius: 46, background: C.grad, display: 'grid', placeItems: 'center' }}>
            <Tick on={at(T.done + 150)} size={24} color={C.indigo} />
          </div>
          <div className="xp-a" style={{ ...rise(done, 80), marginTop: 12, fontSize: 18, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.08 }}>
            {u.outcome[0]}
            <br />
            {u.outcome[1]}
          </div>
          <div className="xp-a xp-mono" style={{ ...rise(done, 160), marginTop: 6, fontSize: 8.5, color: C.muted }}>
            {u.model} · {u.serial}
          </div>
          <div className="xp-a" style={{ ...rise(done, 240), marginTop: 14, borderRadius: 10, boxShadow: `inset 0 0 0 1px ${C.line}` }}>
            {[
              [`${u.Role} credited`, u.partner],
              ['Owner', u.ownerShort],
              ['Checks', `${CHECKS.length} of ${CHECKS.length} · signed`],
            ].map(([k, v], i) => (
              <div key={k} style={{ padding: '7px 10px', borderTop: i ? `1px solid ${C.line}` : 'none' }}>
                <div className="xp-cap" style={{ fontSize: 7.5, color: C.muted }}>{k}</div>
                <div style={{ fontSize: 10.5, fontWeight: 600, marginTop: 1, color: i === 0 ? C.emerald : C.indigo }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </PhoneFrame>

      {/* phone → record */}
      <svg className="xp-abs" style={{ left: 0, top: 0 }} width={480} height={360}>
        <path d="M244 200 C 256 200, 254 168, 268 168" fill="none" stroke={dark ? C.lime : C.emerald} strokeWidth="1.25" strokeDasharray="60" strokeDashoffset={at(T.wire) ? 0 : 60} style={{ transition: 'stroke-dashoffset .4s var(--xe)' }} />
      </svg>

      {/* The manufacturer's record */}
      <div className="xp-abs xp-cap" style={{ left: 268, top: 56, fontSize: 9, color: 'var(--xp-muted)' }}>
        Your record
      </div>
      <div className="xp-abs xp-card" style={{ left: 268, top: 76, width: 196, padding: '12px 0 4px' }}>
        <div style={{ padding: '0 12px' }}>
          <div className="xp-row" style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '-.01em' }}>{u.title}</span>
          </div>
          <div className="xp-row" style={{ justifyContent: 'space-between', marginTop: 3 }}>
            <span className="xp-mono" style={{ fontSize: 8.5, color: C.muted }}>
              {u.serial}
            </span>
            <span
              className="xp-mono"
              style={{
                fontSize: 8,
                fontWeight: 600,
                padding: '2px 7px',
                borderRadius: 20,
                letterSpacing: '.08em',
                background: at(T.wire) ? 'rgba(63,174,108,.14)' : C.alt,
                color: at(T.wire) ? C.emerald : C.muted,
                transition: 'background .2s, color .2s',
              }}
            >
              {at(T.wire) ? 'ACTIVE' : 'SHIPPED'}
            </span>
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          {rows.map(([k, before, after], i) => {
            const on = at(T.fill + i * 110);
            return (
              <div key={k} style={{ padding: '7px 12px', borderTop: `1px solid ${C.line}`, background: on && at(T.fill + i * 110 + 600) ? 'transparent' : on ? 'rgba(63,174,108,.07)' : 'transparent', transition: 'background .5s' }}>
                <div className="xp-cap" style={{ fontSize: 7.5, color: C.muted }}>
                  {k}
                </div>
                <div style={{ position: 'relative', height: 15, overflow: 'hidden', fontSize: 10.5, fontWeight: 600, marginTop: 1 }}>
                  <div className="xp-a" style={{ position: 'absolute', color: '#B3B1CC', fontWeight: 500, transform: on ? 'translateY(-14px)' : 'none', opacity: on ? 0 : 1 }}>
                    {before}
                  </div>
                  <div className="xp-a" style={{ position: 'absolute', whiteSpace: 'nowrap', color: k === u.statusRow[0] ? C.emerald : C.indigo, transform: on ? 'none' : 'translateY(14px)', opacity: on ? 1 : 0 }}>
                    {after}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="xp-abs xp-row xp-cap xp-a" style={{ ...rise(at(T.fill + 900), 0, 6), left: 270, top: 318, gap: 7, fontSize: 8.5, color: 'var(--xp-muted)' }}>
        <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
          <path d="M1 1v3.5a2 2 0 0 0 2 2h7.5M8 4l2.6 2.5L8 9" stroke={dark ? C.lime : C.emerald} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Start date written back to your ERP
      </div>
    </>
  );
}

export const handoverDef: SceneDef = {
  name: 'Handover',
  label: (u) => `The ${u.partnerRole} at ${u.partner} ticks the ${u.form.toLowerCase()} checks on a phone for a ${u.brand} ${u.title}, it is signed, and the record updates the same day with owner, ${u.partnerRole} and ${u.statusRow[0].toLowerCase()}.`,
  W: 480,
  H: 360,
  duration: 9000,
  end: 8400,
  steps: [
    { at: 0, label: 'Pre-delivery checks' },
    { at: T.sheet, label: 'Owner signs' },
    { at: T.done, label: 'Warranty active from today' },
  ],
  Scene: HandoverScene,
};

export const HandoverExplainer = (p: ExplainerProps) => <Explainer def={handoverDef} {...p} />;
